/**
 * The ⋯ overflow menu shared by the drawer header and the planner top bar —
 * the assistant's secondary actions ("New chat", the drawer's history view)
 * plus the usage meter, which deliberately lives HERE rather than pinned in
 * the header (see AssistantUsageMeter). The usage number fetches when the
 * menu opens, so it's always fresh with no refreshKey plumbing.
 */

import { useRef, useState } from 'react';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import AssistantUsageMeter from './AssistantUsageMeter';
import { useAssistantUsage } from './useAssistantUsage';

interface AssistantOverflowMenuProps {
  eventId: string;
  onNewChat: () => void;
  /** Disable "New chat" while a reply is streaming. */
  newChatDisabled?: boolean;
  /** Drawer only — swaps the body to the in-drawer history view. */
  onOpenHistory?: () => void;
}

/** Inner section so the usage fetch only runs while the menu is open. */
const UsageSection = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const used = useAssistantUsage(eventId);
  if (used == null) return null;
  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        pb: 0.5,
        mt: 0.5,
        minWidth: 240,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.75 }}
      >
        {t('assistant.menu.usage')}
      </Typography>
      <AssistantUsageMeter value={used} />
    </Box>
  );
};

const AssistantOverflowMenu = ({
  eventId,
  onNewChat,
  newChatDisabled,
  onOpenHistory,
}: AssistantOverflowMenuProps) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <Tooltip title={t('assistant.drawer.menu_aria')}>
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={() => setOpen(true)}
          aria-label={t('assistant.drawer.menu_aria')}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
        >
          <IconifyIcon
            icon="material-symbols:more-horiz"
            sx={{ fontSize: 20, color: 'neutral.dark' }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Pin the width to the usage section's, so the menu opens at its FINAL
        // size. Otherwise it opens narrow (usage fetch still pending), MUI
        // right-anchors that narrow paper, then the async usage row widens it —
        // growing rightward off-screen and clipping the "available" caption
        // when the ⋯ sits hard against the corner (the planner top bar).
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <MenuItem
          disabled={newChatDisabled}
          onClick={() => {
            close();
            onNewChat();
          }}
        >
          <ListItemIcon>
            <IconifyIcon icon="material-symbols:edit-square-outline-rounded" />
          </ListItemIcon>
          <ListItemText>{t('assistant.drawer.new_chat')}</ListItemText>
        </MenuItem>
        {onOpenHistory && (
          <MenuItem
            onClick={() => {
              close();
              onOpenHistory();
            }}
          >
            <ListItemIcon>
              <IconifyIcon icon="material-symbols:history-rounded" />
            </ListItemIcon>
            <ListItemText>{t('assistant.history.open')}</ListItemText>
          </MenuItem>
        )}
        {open && <UsageSection eventId={eventId} />}
      </Menu>
    </>
  );
};

export default AssistantOverflowMenu;
